/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    lazy,
    Suspense,
    useState,
    useEffect,
    useRef,
    useCallback,
    type FocusEvent,
    type MouseEvent,
    type PointerEvent,
    type ReactElement,
    type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { DynamicImage } from '@/components/dynamic-image';
import { preloadDynamicImage } from '@/components/dynamic-image/preload';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { UITarget } from '@/targets/ui-target';
import fibblIcon from '@/components/fibbl/fibbl-icon.svg';
import { useFibblRuntime } from '@/components/fibbl/fibbl-runtime';
import { useDeferredUnmount } from '@/hooks/use-deferred-unmount';

// RGBARRY-CUSTOMIZATION: keep the PDP zoom viewer and dialog/gesture code out of the initial PDP bundle.
const ProductImageZoom = lazy(() => import('@/components/product-image-zoom'));

export interface GalleryImage {
    src: string;
    alt?: string;
    thumbSrc?: string;
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer + Vimeo video gallery support
    /** When 'video', src is the Vimeo poster (thumbnail) and vimeoId drives iframe on interaction. */
    type?: 'image' | 'video' | 'fibbl';
    vimeoId?: string;
    /** When type is 'fibbl', the product ID to pass to fibbl-model-viewer. */
    fibblProductId?: string;
    // END RGBARRY-CUSTOMIZATION
}

interface ImageGalleryWidths {
    /**
     * Responsive widths for the main image. Defaults match PDP (`section-container` → `lg:grid-cols-2` →
     * `max-w-screen-2xl`): viewport-wide below `lg`, half-viewport at `lg`/`xl`, capped at 680 once the
     * container hits `max-w-screen-2xl`. Override when the gallery sits in a narrower container (modal,
     * card cell) so DIS doesn't deliver an oversized main image.
     */
    main?: DynamicImageDimensions;
    /**
     * Responsive widths for thumbnails in grid mode. Defaults match PDP (gallery half-width at `lg+`,
     * grid-cols-4). Override when the gallery's effective width differs (e.g. inside a modal at `lg+` or
     * a multi-column card grid). Ignored when `horizontalThumbnails` is set — the strip uses fixed CSS
     * sizes instead.
     */
    thumbnail?: DynamicImageDimensions;
}

interface ImageGalleryProps {
    images: GalleryImage[];
    eager?: boolean;
    /** Show prev/next arrows on the main image (e.g. in modal) */
    showNavigationArrows?: boolean;
    /** Size of navigation arrows: "sm" (default) or "lg" for PDP */
    navigationArrowSize?: 'sm' | 'lg';
    /** Extra classes applied to the nav arrow buttons (e.g. to strip the default background/shadow) */
    navigationArrowClassName?: string;
    /** Use horizontal scrollable thumbnail strip with arrows instead of grid */
    horizontalThumbnails?: boolean;
    /**
     * On desktop: render a 2-column masonry-style tile grid (large hero left, remaining
     * images in a 2-col grid to the right). Mobile always renders the single hero + strip.
     */
    tileGrid?: boolean;
    /** Labels to overlay on the first image as top-left violator badges (PLP-tile style). */
    firstImageBadges?: string[];
    /**
     * RGBARRY-CUSTOMIZATION: node overlaid at the top-right of the mobile hero image (e.g. wishlist heart).
     * Rendered only in the mobile hero (below `lg`); desktop layout is unaffected.
     */
    heroOverlay?: ReactNode;
    productName?: string;
    /**
     * Per-callsite responsive widths for the main image and grid thumbnails. Either field may be
     * omitted to keep the corresponding default. Defaults are PDP-shaped (full container,
     * `lg:grid-cols-2`, capped at `max-w-screen-2xl`); override when the gallery sits in a narrower
     * container so DIS doesn't deliver oversized variants.
     */
    widths?: ImageGalleryWidths;
    /** Show an interaction-loaded zoom affordance on the first product image. */
    enableZoom?: boolean;
}

const THUMBNAIL_SCROLL_OFFSET = 200;

// === Canonical width ladder for cache reuse ===
//
// Each width here turns into a `sw=<n>` query param on the DIS URL, and `sw` is part of the cache key. A single
// browsing session typically hits the gallery on the PDP, the cart-edit modal, the bonus-product modal, and the
// per-child gallery on a set/bundle PDP. If those four surfaces pick four slightly different widths for the same
// product image, the browser and CDN cache them as four separate variants. Same picture, four downloads.
//
// Fix: pick widths from a small shared ladder instead of fitting each container to the pixel. ~3-5% over-supply is
// invisible to the user; the cache reuse compounds across navigation. When in doubt, round up to the next rung —
// never invent a new one.
//
//   thumbs:  64, 80, 96, 144, 176, 240
//   main:    360, 420, 680, '100vw' (resolves to 640/768/1024), '50vw'
//
// Alignments this currently buys us:
//   - All mobile main images share the `'100vw'` rung → 640/768/1024 on PDP, cart-modal, bonus-modal.
//   - All desktop "narrow gallery" main images share 420 (cart-modal `md+`, bonus-modal `lg+`, child-card `md+`).
//   - PDP and bonus-modal share the same `md` thumb at 240.
//
// PDP gallery is full-width below `lg`, half-width at `lg`/`xl` (`lg:grid-cols-2`), and capped at 680 once the
// container hits `max-w-screen-2xl`. The `'50vw'` overestimates slightly (ignores `lg:gap-12`) which is harmless —
// the browser picks the smallest sufficient candidate from the srcset.
const DEFAULT_WIDTHS_MAIN: DynamicImageDimensions = { base: '100vw', lg: '50vw', '2xl': 680 };
// Grid is `grid-cols-4 gap-2 sm:gap-3` inside the gallery container, and the gallery itself drops to half-width once
// PDP switches to `lg:grid-cols-2`. Cell width therefore peaks in the `md` range (~230px just below `lg`) and falls
// back to ~160px at `lg+` where the gallery is half-width and capped by `max-w-screen-2xl`. Each entry targets the
// upper end of its breakpoint range so DIS still has headroom at @2x DPR.
const DEFAULT_WIDTHS_THUMBNAIL_GRID: DynamicImageDimensions = { base: 144, sm: 176, md: 240, lg: 168 };
// Horizontal strip uses fixed `h-16 w-16 sm:h-20 sm:w-20` — 64 base, 80 sm+. Fixed CSS, so this is intentionally
// not configurable per consumer.
const DEFAULT_WIDTHS_THUMBNAIL_STRIP: DynamicImageDimensions = [64, 80];

/** Poster URL for a Vimeo video via free vumbnail service — no auth, no API call. */
const vimeoPoster = (id: string): string => `https://vumbnail.com/${id}.jpg`;

/**
 * Click-to-play Vimeo slide. Renders a poster + play icon until activated; then swaps in the iframe.
 * `fill=true` when used as the main image (absolute-inset), `fill=false` for a thumbnail tile.
 */
function VimeoSlide({
    vimeoId,
    alt,
    fill = false,
    className,
}: {
    vimeoId: string;
    alt: string;
    fill?: boolean;
    className?: string;
}) {
    const [playing, setPlaying] = useState(false);
    const wrapCls = cn('relative overflow-hidden bg-[#F4F4F4]', fill ? 'w-full h-full' : 'w-full h-full', className);
    if (playing) {
        return (
            <div className={wrapCls}>
                <iframe
                    src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&controls=1&autopause=1&loop=0`}
                    title={alt}
                    allow="autoplay; fullscreen; picture-in-picture"
                    className="absolute inset-0 w-full h-full"
                    frameBorder={0}
                />
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                setPlaying(true);
            }}
            className={cn(wrapCls, 'group cursor-pointer')}
            aria-label={`Play video: ${alt}`}>
            <img
                src={vimeoPoster(vimeoId)}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background/90 shadow-lg transition-transform group-hover:scale-110">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 translate-x-0.5 text-[#1a1a1a]">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                </span>
            </span>
        </button>
    );
}

// RGBARRY-CUSTOMIZATION: FibblTile — desktop 3D viewer tile, click-to-load.
// Before the shopper opts in, the tile is a static poster (the product-image fallback) with a
// "View in 3D" affordance — no Fibbl runtime is loaded. Clicking requests the ~305KB runtime via
// the shared FibblRuntimeProvider; the live <fibbl-model-viewer> then mounts with a loading spinner.
// On fibbl-load-error the viewer is swapped for the fallback image *in place* (the cell stays
// mounted) so the tile grid never reflows — the PDP CLS fix from #228 is preserved here.
function FibblTile({ productId, fallback }: { productId: string; fallback: ReactNode }) {
    const { requested, request } = useFibblRuntime();
    const [loaded, setLoaded] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const ref = useRef<HTMLElement | null>(null);

    useEffect(() => {
        // The viewer only mounts once the runtime is requested, so there's nothing to listen to
        // before then. Re-run when `requested` flips so the freshly-mounted element gets its listeners.
        if (!requested) return;
        const el = ref.current;
        if (!el) return;
        const onLoad = () => setLoaded(true);
        // fibbl-load-error fires when no 3D model exists for this product. Swap the
        // viewer for the fallback image *in place* rather than unmounting the tile:
        // removing the tile from the grid reflows every sibling tile and shifts the
        // layout well after first paint (a large PDP CLS contributor, worst on desktop
        // where the whole tile grid is visible). Keeping the cell mounted holds the slot.
        const onError = () => setUnavailable(true);
        el.addEventListener('fibbl-load-end', onLoad);
        el.addEventListener('fibbl-load-error', onError);
        return () => {
            el.removeEventListener('fibbl-load-end', onLoad);
            el.removeEventListener('fibbl-load-error', onError);
        };
    }, [requested]);

    if (unavailable) {
        return <>{fallback}</>;
    }

    // Not yet opted in: static poster + "View in 3D" affordance. No runtime, no model assets.
    if (!requested) {
        return (
            <button
                type="button"
                onClick={request}
                aria-label="View in 3D"
                className="group relative w-full h-full overflow-hidden bg-[#F4F4F4] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                {fallback}
                <span className="absolute inset-0 flex items-center justify-center bg-transparent transition-colors group-hover:bg-[#1a1a1a]/5">
                    <span className="flex items-center gap-2 bg-background/90 px-4 py-2 shadow-md transition-transform group-hover:scale-105">
                        <img src={fibblIcon} alt="" aria-hidden="true" className="h-5 w-5" />
                        <span className="text-[11px] font-medium tracking-[0.08em] uppercase text-[#1a1a1a]">
                            View in 3D
                        </span>
                    </span>
                </span>
            </button>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {!loaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F4F4F4] z-10 pointer-events-none">
                    <div className="w-8 h-8 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
                    <span className="text-[11px] tracking-widest uppercase text-[#1a1a1a]/50">Loading 3D</span>
                </div>
            )}
            <fibbl-model-viewer
                key={productId}
                data-product-id={productId}
                ref={(el: HTMLElement | null) => {
                    ref.current = el;
                }}
                style={{ display: 'block', width: '100%', height: '100%' }}
            />
        </div>
    );
}
// END RGBARRY-CUSTOMIZATION

function TileGrid({
    images,
    eager,
    tileWidths,
    imageAltFallback,
    handleThumbnailClick,
    firstImageBadges,
    firstImageZoomControl,
}: {
    images: GalleryImage[];
    eager: boolean;
    tileWidths: DynamicImageDimensions;
    imageAltFallback: string;
    handleThumbnailClick: (e: MouseEvent<HTMLButtonElement>) => void;
    firstImageBadges?: string[];
    firstImageZoomControl?: ReactNode;
}) {
    const [showAll, setShowAll] = useState(false);
    const hasMore = images.length > 4;
    const visibleImages = showAll ? images : images.slice(0, 4);
    // RGBARRY-CUSTOMIZATION: fallback for the Fibbl tile when no 3D model exists. Prefer a real
    // product image that isn't already rendered in a visible tile so the Fibbl slot never duplicates
    // one on screen; fall back to any product image if every one is already visible (a duplicate is
    // still better than a collapsed/blank cell). Comparing against `visibleImages` (not a fixed
    // 4-tile slice) keeps the dedup correct for small galleries (≤4 images) and when "show all" is on.
    // This keeps the tile's grid cell reserved on `fibbl-load-error` (see FibblTile) so the
    // layout can't shift — instead of removing the tile and reflowing the grid.
    const isProductImage = (img: GalleryImage) => img.type !== 'fibbl' && img.type !== 'video';
    const visibleProductSrcs = new Set(visibleImages.filter(isProductImage).map((img) => img.src));
    const fibblFallbackImage =
        images.find((img) => isProductImage(img) && !visibleProductSrcs.has(img.src)) ?? images.find(isProductImage);
    // END RGBARRY-CUSTOMIZATION

    return (
        <div className="hidden lg:grid grid-cols-2 gap-6" data-gallery-tiles>
            {visibleImages.map((image, index) => {
                const isLastVisible = !showAll && hasMore && index === 3;
                const tileKey =
                    image.type === 'video'
                        ? `video-${image.vimeoId}`
                        : image.type === 'fibbl'
                          ? `fibbl-${image.fibblProductId}`
                          : image.src;
                return (
                    <div key={tileKey} className="relative aspect-square">
                        {/* RGBARRY-CUSTOMIZATION: Fibbl tile at slot 2 */}
                        {image.type === 'fibbl' && image.fibblProductId ? (
                            <FibblTile
                                productId={image.fibblProductId}
                                fallback={
                                    fibblFallbackImage ? (
                                        <div className="relative w-full h-full overflow-hidden bg-[#F4F4F4]">
                                            <DynamicImage
                                                src={fibblFallbackImage.src}
                                                alt={fibblFallbackImage.alt || imageAltFallback}
                                                widths={tileWidths}
                                                className="w-full h-full"
                                                imageProps={{
                                                    className: 'w-full h-full object-contain mix-blend-multiply',
                                                }}
                                                loading="lazy"
                                            />
                                        </div>
                                    ) : null
                                }
                            />
                        ) : image.type === 'video' && image.vimeoId ? (
                            <VimeoSlide vimeoId={image.vimeoId} alt={image.alt || imageAltFallback} fill />
                        ) : (
                            <button
                                data-index={index}
                                onClick={handleThumbnailClick}
                                className="relative w-full h-full overflow-hidden bg-[#F4F4F4] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
                                <DynamicImage
                                    src={image.src}
                                    alt={image.alt || imageAltFallback}
                                    widths={tileWidths}
                                    className="w-full h-full"
                                    imageProps={{ className: 'w-full h-full object-contain mix-blend-multiply' }}
                                    // RGBARRY-CUSTOMIZATION: the first tile owns the single responsive LCP preload.
                                    // Its widths match the visible mobile hero below lg and this tile at lg+.
                                    loading={index === 0 && eager ? 'eager' : 'lazy'}
                                    priority={index === 0 && eager ? 'high' : undefined}
                                />
                            </button>
                        )}
                        {index === 0 && firstImageBadges && firstImageBadges.length > 0 && (
                            <div className="pointer-events-none absolute top-2 left-2 flex flex-col items-start gap-1 z-20">
                                {firstImageBadges.map((label) => (
                                    <span
                                        key={label}
                                        data-slot="badge"
                                        className="px-2 py-1 text-xs font-semibold uppercase inline-block bg-foreground text-background leading-none rounded-ui">
                                        {label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {index === 0 && firstImageZoomControl}
                        {isLastVisible && (
                            <button
                                type="button"
                                onClick={() => setShowAll(true)}
                                className="absolute bottom-3 right-3 bg-background border border-[#d0d0d0] text-[#1a1a1a] text-[11px] font-medium tracking-[0.06em] uppercase px-3 py-2 hover:bg-[#f5f5f5] transition-colors shadow-sm z-10">
                                View all {images.length} images
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function ImageGallery({
    images,
    eager = false,
    showNavigationArrows = false,
    navigationArrowSize = 'sm',
    navigationArrowClassName,
    horizontalThumbnails = false,
    tileGrid = false,
    firstImageBadges,
    heroOverlay,
    productName,
    widths,
    enableZoom = false,
}: ImageGalleryProps): ReactElement {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const thumbStripRef = useRef<HTMLDivElement>(null);
    const config = useConfig();
    // RGBARRY-CUSTOMIZATION: shared click-to-load Fibbl runtime state (inert outside a provider, e.g.
    // the gallery in a cart/bonus modal, which never carries a Fibbl entry anyway).
    const { requested: fibblRequested, request: requestFibbl } = useFibblRuntime();
    const mainWidths = widths?.main ?? DEFAULT_WIDTHS_MAIN;
    const thumbnailWidths = widths?.thumbnail ?? DEFAULT_WIDTHS_THUMBNAIL_GRID;
    // RGBARRY-CUSTOMIZATION: defer both the zoom viewer chunk and its full-resolution image until intent.
    const [zoomOpen, setZoomOpen] = useState(false);
    const zoomMounted = useDeferredUnmount(zoomOpen);
    const zoomTriggerRef = useRef<HTMLButtonElement | null>(null);

    const preloadGallerySlide = useCallback(
        (image: GalleryImage | undefined) => {
            if (!image) {
                return;
            }
            preloadDynamicImage({
                config,
                src: image.src,
                widths: mainWidths,
                fetchPriority: 'low',
            });
        },
        [config, mainWidths]
    );

    /** Promote a slide on hover/focus intent so it can enter the cache before the click lands. */
    const handleThumbnailIntent = useCallback(
        (event: PointerEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => {
            const index = Number(event.currentTarget.dataset.index);
            if (Number.isNaN(index)) {
                return;
            }
            preloadGallerySlide(images[index]);
        },
        [images, preloadGallerySlide]
    );
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = thumbStripRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = thumbStripRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener('scroll', updateScrollState, { passive: true });
        const ro = new ResizeObserver(updateScrollState);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            ro.disconnect();
        };
    }, [images, updateScrollState]);

    const handleScrollThumbnailsLeft = useCallback(() => {
        thumbStripRef.current?.scrollBy({ left: -THUMBNAIL_SCROLL_OFFSET, behavior: 'smooth' });
    }, []);
    const handleScrollThumbnailsRight = useCallback(() => {
        thumbStripRef.current?.scrollBy({ left: THUMBNAIL_SCROLL_OFFSET, behavior: 'smooth' });
    }, []);
    const handleThumbnailClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        const nextIndex = Number(event.currentTarget.dataset.index);
        if (!Number.isNaN(nextIndex)) {
            setSelectedImageIndex(nextIndex);
        }
    }, []);

    useEffect(() => {
        // When images change (e.g., color variant changes), try to preserve the selected index
        // Only reset to 0 if the current index is out of bounds for the new images array
        // The key prop ensures each product has independent state, so this only affects the current product
        setSelectedImageIndex((currentIndex) => {
            // If current index is still valid for the new images array, keep it
            // Otherwise reset to 0
            return currentIndex < images.length ? currentIndex : 0;
        });
    }, [images]);

    // RGBARRY-CUSTOMIZATION: do not proactively preload unselected slides. Chrome reports those links as unused and
    // they consume bandwidth for images most shoppers never open; thumbnail hover/focus still preloads on intent.

    const { t: tCommon } = useTranslation('common');
    const { t: tProduct } = useTranslation('product');

    // The first image is the fallback image. It's needed for when `images` are just updated, and the `selectedImageIndex` goes out of bound and is soon to be reset.
    const selectedImage = images[selectedImageIndex] ?? images[0];

    // RGBARRY-CUSTOMIZATION: on mobile the 3D thumbnail is the opt-in — selecting it activates the hero,
    // so requesting the runtime here loads it lazily on that tap (there's no separate poster to click).
    useEffect(() => {
        if (selectedImage?.type === 'fibbl' && !fibblRequested) requestFibbl();
    }, [selectedImage, fibblRequested, requestFibbl]);

    if (!images || images.length === 0) {
        return (
            <div className="aspect-square bg-muted flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                    <div className="text-4xl mb-2">📷</div>
                    <p>{tCommon('noImageAvailable')}</p>
                </div>
            </div>
        );
    }

    const imageAltFallback = productName || tProduct('imageAlt') || 'Product Image';
    const firstImage = images[0];
    const canZoomFirstImage = enableZoom && firstImage?.type !== 'video' && firstImage?.type !== 'fibbl';
    // RGBARRY-CUSTOMIZATION: pass metadata only; the zoom overlay renders one hi-res image at a time on navigation.
    const zoomImages = images.filter((image) => image.type !== 'video' && image.type !== 'fibbl');
    const openZoom = (event: MouseEvent<HTMLButtonElement>) => {
        zoomTriggerRef.current = event.currentTarget;
        setZoomOpen(true);
    };
    const zoomControl = canZoomFirstImage ? (
        <button
            type="button"
            onClick={openZoom}
            className="absolute bottom-3 right-3 z-30 flex size-11 items-center justify-center border border-[#d0d0d0] bg-background text-foreground shadow-sm transition-colors hover:bg-[#f5f5f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={tCommon('zoomProductImage', { defaultValue: 'Zoom product image' })}>
            <ZoomIn className="size-5" aria-hidden="true" />
        </button>
    ) : null;
    const zoomOverlay =
        zoomMounted && canZoomFirstImage ? (
            <Suspense fallback={null}>
                <ProductImageZoom
                    open={zoomOpen}
                    onOpenChange={setZoomOpen}
                    images={zoomImages}
                    productName={productName}
                    restoreFocus={() => zoomTriggerRef.current?.focus()}
                />
            </Suspense>
        ) : null;

    // ── Tile grid layout (desktop PDP) ─────────────────────────────────────────
    // Large hero left, up to 3 additional images in a 2-col grid to the right.
    // Clicking any tile sets that image as the active "selected" for the mobile
    // single-image fallback; on desktop all tiles are always visible.
    // Mobile always shows the standard hero + horizontal strip.
    if (tileGrid) {
        // RGBARRY-CUSTOMIZATION: one responsive preload serves the visible first image at each layout:
        // the full-width mobile hero below lg and the quarter-viewport desktop tile at lg+.
        const tileWidths = { base: '100vw', lg: '25vw', '2xl': 420 };
        return (
            <UITarget targetId="sfcc.pdp.products.gallery">
                {/* Mobile: standard hero + strip */}
                <div className="lg:hidden space-y-4">
                    <div className="relative aspect-square overflow-hidden bg-[#F4F4F4]">
                        {/* RGBARRY-CUSTOMIZATION: Fibbl 3D viewer on mobile hero — click-to-load. The viewer
                            mounts only after the runtime is requested (the effect above fires on selecting
                            the 3D thumbnail); until then a static placeholder holds the slot. */}
                        {selectedImage.type === 'fibbl' && selectedImage.fibblProductId ? (
                            fibblRequested ? (
                                <fibbl-model-viewer
                                    key={selectedImage.fibblProductId}
                                    data-product-id={selectedImage.fibblProductId}
                                    style={{ display: 'block', width: '100%', height: '100%' }}
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F4F4F4]">
                                    <img src={fibblIcon} alt="" aria-hidden="true" className="h-12 w-12 opacity-60" />
                                    <span className="text-[11px] tracking-widest uppercase text-[#1a1a1a]/50">
                                        Loading 3D
                                    </span>
                                </div>
                            )
                        ) : selectedImage.type === 'video' && selectedImage.vimeoId ? (
                            <VimeoSlide
                                vimeoId={selectedImage.vimeoId}
                                alt={selectedImage.alt || imageAltFallback}
                                fill
                            />
                        ) : (
                            <DynamicImage
                                src={selectedImage.src}
                                alt={selectedImage.alt || imageAltFallback}
                                // RGBARRY-CUSTOMIZATION: share the priority tile's breakpoint-aware source set.
                                // The hidden mobile copy stays lazy on desktop; below lg the priority tile's preload
                                // warms this exact full-width resource before the visible hero requests it.
                                widths={tileWidths}
                                className="w-full h-full"
                                imageProps={{ className: 'w-full h-full object-contain mix-blend-multiply' }}
                                loading="lazy"
                            />
                        )}
                        {selectedImageIndex === 0 && firstImageBadges && firstImageBadges.length > 0 && (
                            <div className="pointer-events-none absolute top-2 left-2 flex flex-col items-start gap-1 z-20">
                                {firstImageBadges.map((label) => (
                                    <span
                                        key={label}
                                        data-slot="badge"
                                        className="px-2 py-1 text-xs font-semibold uppercase inline-block bg-foreground text-background leading-none rounded-ui">
                                        {label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {showNavigationArrows && images.length > 1 && (
                            <ImageNavArrows
                                imageCount={images.length}
                                onIndexChange={setSelectedImageIndex}
                                size={navigationArrowSize}
                                className={navigationArrowClassName}
                            />
                        )}
                        {/* RGBARRY-CUSTOMIZATION: mobile-only overlay (wishlist heart) pinned to the hero's top-right */}
                        {heroOverlay}
                        {selectedImageIndex === 0 && zoomControl}
                    </div>
                    {images.length > 1 && (
                        <div className="relative flex items-center gap-2">
                            {canScrollLeft && (
                                <button
                                    type="button"
                                    onClick={handleScrollThumbnailsLeft}
                                    className="hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center border border-border bg-background shadow-md hover:bg-muted transition-colors"
                                    aria-label={tCommon('previousImage')}>
                                    <ChevronLeft className="size-4" />
                                </button>
                            )}
                            <div
                                ref={thumbStripRef}
                                className="flex flex-1 gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                {images.map((image, index) => {
                                    const isVideo = image.type === 'video' && Boolean(image.vimeoId);
                                    const isFibbl = image.type === 'fibbl';
                                    const key = isFibbl
                                        ? `fibbl-${image.fibblProductId}`
                                        : isVideo
                                          ? `video-${image.vimeoId}`
                                          : image.src + (image.thumbSrc || '');
                                    return (
                                        <button
                                            key={key}
                                            onClick={handleThumbnailClick}
                                            onPointerEnter={handleThumbnailIntent}
                                            onFocus={handleThumbnailIntent}
                                            data-index={index}
                                            className={cn(
                                                'relative flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 overflow-hidden bg-[#F4F4F4] border-2 transition-colors cursor-pointer',
                                                selectedImageIndex === index
                                                    ? 'border-primary'
                                                    : 'border-transparent hover:border-border'
                                            )}>
                                            {/* RGBARRY-CUSTOMIZATION: Fibbl icon in mobile thumbnail strip */}
                                            {isFibbl ? (
                                                <span className="absolute inset-0 flex items-center justify-center p-2">
                                                    <img
                                                        src={fibblIcon}
                                                        alt="View in 3D"
                                                        className="w-full h-full object-contain"
                                                    />
                                                </span>
                                            ) : isVideo ? (
                                                <>
                                                    <img
                                                        src={vimeoPoster(image.vimeoId ?? '')}
                                                        alt={image.alt || imageAltFallback}
                                                        loading="lazy"
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                    <span className="absolute inset-0 flex items-center justify-center">
                                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background/90 shadow">
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                                className="h-3 w-3 translate-x-px text-[#1a1a1a]">
                                                                <path d="M8 5v14l11-7z" />
                                                            </svg>
                                                        </span>
                                                    </span>
                                                </>
                                            ) : (
                                                <DynamicImage
                                                    src={image.thumbSrc || image.src}
                                                    alt={image.alt || imageAltFallback}
                                                    widths={DEFAULT_WIDTHS_THUMBNAIL_STRIP}
                                                    className="w-full h-full"
                                                    imageProps={{
                                                        className:
                                                            'w-full h-full object-cover object-center mix-blend-multiply',
                                                    }}
                                                    loading="lazy"
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            {canScrollRight && (
                                <button
                                    type="button"
                                    onClick={handleScrollThumbnailsRight}
                                    className="hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center border border-border bg-background shadow-md hover:bg-muted transition-colors"
                                    aria-label={tCommon('nextImage')}>
                                    <ChevronRight className="size-4" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Desktop: 2-col tile grid */}
                <TileGrid
                    images={images}
                    eager={eager}
                    tileWidths={tileWidths}
                    imageAltFallback={imageAltFallback}
                    handleThumbnailClick={handleThumbnailClick}
                    firstImageBadges={firstImageBadges}
                    firstImageZoomControl={zoomControl}
                />
                {zoomOverlay}
            </UITarget>
        );
    }

    return (
        <UITarget targetId="sfcc.pdp.products.gallery">
            <div className="space-y-4">
                {/* Main Image */}
                <div className="relative aspect-square overflow-hidden bg-[#F4F4F4]" data-gallery-hero>
                    <DynamicImage
                        src={selectedImage.src}
                        alt={selectedImage.alt || imageAltFallback}
                        widths={mainWidths}
                        className="w-full h-full object-cover object-center [&_img]:object-contain! [&_img]:h-full! [&_img]:max-w-full! [&_img]:mx-auto! [&_img]:mix-blend-multiply"
                        loading={eager ? 'eager' : 'lazy'}
                        priority={eager ? 'high' : undefined}
                    />
                    {showNavigationArrows && images.length > 1 && (
                        <ImageNavArrows
                            imageCount={images.length}
                            onIndexChange={setSelectedImageIndex}
                            size={navigationArrowSize}
                            className={navigationArrowClassName}
                        />
                    )}
                    {selectedImageIndex === 0 && zoomControl}
                </div>

                {/* Thumbnail Navigation */}
                {images.length > 1 && !horizontalThumbnails && (
                    <div
                        className="grid grid-cols-4 gap-2 sm:gap-3"
                        data-gallery-thumbs
                        role="group"
                        aria-label={imageAltFallback}>
                        {images.map((image, index) => (
                            <button
                                key={image.src + (image.thumbSrc || '')}
                                onClick={handleThumbnailClick}
                                onPointerEnter={handleThumbnailIntent}
                                onFocus={handleThumbnailIntent}
                                data-index={index}
                                aria-label={tCommon('thumbnailImage', { current: index + 1, total: images.length })}
                                // The selected thumbnail is the current item within a single-select set, not an
                                // independently toggleable control, so aria-current ("current" in a set) fits better
                                // than aria-pressed ("toggle button, pressed"). Mirrors the swatch / pagination
                                // selection pattern used elsewhere. A full role="tab" widget was considered and
                                // rejected: its roving-tabindex + arrow-key contract would be a large, regression-prone
                                // change on a gallery reused across PDP and several modals for a semantic refinement.
                                aria-current={selectedImageIndex === index ? 'true' : undefined}
                                className={`
                                aspect-square overflow-hidden bg-[#F4F4F4]
                                border-2 transition-colors cursor-pointer
                                ${
                                    selectedImageIndex === index
                                        ? 'border-primary'
                                        : 'border-transparent hover:border-border'
                                }
                            `}>
                                <DynamicImage
                                    src={image.thumbSrc || image.src}
                                    alt=""
                                    widths={thumbnailWidths}
                                    className="w-full h-full"
                                    imageProps={{
                                        className: 'w-full h-full object-cover object-center mix-blend-multiply',
                                    }}
                                    loading="lazy"
                                />
                            </button>
                        ))}
                    </div>
                )}

                {/* Horizontal Scrollable Thumbnail Strip */}
                {images.length > 1 && horizontalThumbnails && (
                    <div className="relative flex items-center gap-2">
                        {canScrollLeft && (
                            <button
                                type="button"
                                onClick={handleScrollThumbnailsLeft}
                                className={cn(
                                    'hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center border border-border bg-background shadow-md',
                                    'hover:bg-muted transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}
                                aria-label={tCommon('previousImage')}>
                                <ChevronLeft className="size-4" />
                            </button>
                        )}
                        <div
                            ref={thumbStripRef}
                            data-gallery-strip
                            role="group"
                            aria-label={imageAltFallback}
                            className="flex flex-1 gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            {images.map((image, index) => (
                                <button
                                    key={image.src + (image.thumbSrc || '')}
                                    onClick={handleThumbnailClick}
                                    onPointerEnter={handleThumbnailIntent}
                                    onFocus={handleThumbnailIntent}
                                    data-index={index}
                                    aria-label={tCommon('thumbnailImage', { current: index + 1, total: images.length })}
                                    // See the grid layout above: aria-current, not aria-pressed, for the selected slide.
                                    aria-current={selectedImageIndex === index ? 'true' : undefined}
                                    className={cn(
                                        'flex-shrink-0 h-16 w-16 sm:h-20 sm:w-20 overflow-hidden bg-[#F4F4F4]',
                                        'border-2 transition-colors cursor-pointer',
                                        selectedImageIndex === index
                                            ? 'border-primary'
                                            : 'border-transparent hover:border-border'
                                    )}>
                                    <DynamicImage
                                        src={image.thumbSrc || image.src}
                                        alt=""
                                        widths={DEFAULT_WIDTHS_THUMBNAIL_STRIP}
                                        className="w-full h-full"
                                        imageProps={{
                                            className: 'w-full h-full object-cover object-center mix-blend-multiply',
                                        }}
                                        loading="lazy"
                                    />
                                </button>
                            ))}
                        </div>
                        {canScrollRight && (
                            <button
                                type="button"
                                onClick={handleScrollThumbnailsRight}
                                className={cn(
                                    'hidden sm:flex h-8 w-8 flex-shrink-0 items-center justify-center border border-border bg-background shadow-md',
                                    'hover:bg-muted transition-colors',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                )}
                                aria-label={tCommon('nextImage')}>
                                <ChevronRight className="size-4" />
                            </button>
                        )}
                    </div>
                )}
                {zoomOverlay}
            </div>
        </UITarget>
    );
}
